import { useState } from 'react';
import { Target, Users, MessageSquareQuote, Calendar, Radio, FileText, Link2, ChevronDown, ChevronUp, Pencil, Save, X } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import { Badge } from '../ui/Badge';
import { CANAIS, TIPOS } from '../../lib/constants';
import { parseDate, formatPeriodo, duracaoTexto, acaoAcontecendoAgora } from '../../lib/dates';

function labelFromList(list, keys) {
  return (keys || []).map((k) => list.find((i) => i.key === k)?.label || k).filter(Boolean);
}

function isUrl(v) {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

// Detalhes da demanda — visão estruturada em seções para a Comunicação
// produzir a peça com tudo à vista. Apenas seções com conteúdo aparecem.
//
// `podeEditar` (só Comunicação/admin) liga a edição inline do OBJETIVO —
// pedido da Laila (07/07): além das datas, ela precisava corrigir o texto do
// objetivo direto na demanda sem passar pela Comunicação. Backend já aceita
// `objetivo` no PATCH e registra na Timeline (demanda_alteracoes).
export function DetalhesCompactos({ demanda, podeEditar = false, onSalvo }) {
  const canais = labelFromList(CANAIS, demanda.canais);
  const tipos = labelFromList(TIPOS, demanda.tipos);

  const [editObj, setEditObj] = useState(false);
  const [objetivo, setObjetivo] = useState(demanda.objetivo || '');
  const [savingObj, setSavingObj] = useState(false);
  const [erroObj, setErroObj] = useState(null);

  const abrirEdicaoObjetivo = () => {
    setObjetivo(demanda.objetivo || '');
    setErroObj(null);
    setEditObj(true);
  };

  const salvarObjetivo = async () => {
    setErroObj(null);
    if (objetivo.trim().length < 3) {
      setErroObj('O objetivo precisa ter ao menos 3 caracteres.');
      return;
    }
    setSavingObj(true);
    try {
      await api.patch(`/demandas/${demanda.codigo}`, { objetivo: objetivo.trim() });
      setEditObj(false);
      onSalvo?.();
    } catch (e) {
      setErroObj(e.data?.error || e.message);
    } finally { setSavingObj(false); }
  };

  const briefing = [
    demanda.publico_alvo && { label: 'Público-alvo', value: demanda.publico_alvo, icon: Users },
    demanda.mensagem_chave && { label: 'Mensagem-chave', value: demanda.mensagem_chave, icon: MessageSquareQuote },
  ].filter(Boolean);

  const duracao = duracaoTexto(demanda.data_acao, demanda.data_acao_fim);
  const acontecendo = acaoAcontecendoAgora(demanda.data_acao, demanda.data_acao_fim);
  const datas = [
    demanda.data_acao && {
      label: 'Data da ação',
      value: (
        <span>
          {formatPeriodo(demanda.data_acao, demanda.data_acao_fim)}
          {duracao && <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>· {duracao}</span>}
          {acontecendo && <Badge color="success" style={{ marginLeft: 6 }}>Acontecendo agora</Badge>}
        </span>
      ),
    },
    demanda.data_postagem && { label: 'Postagem prevista', value: format(parseDate(demanda.data_postagem), 'dd/MM/yyyy') },
  ].filter(Boolean);

  const meios = [
    demanda.meio_instagram && { label: 'Instagram', value: demanda.meio_instagram },
    demanda.meio_portais && { label: 'Portais', value: demanda.meio_portais },
    demanda.meio_radio && { label: 'Rádio', value: demanda.meio_radio },
    demanda.meio_tv && { label: 'TV', value: demanda.meio_tv },
  ].filter(Boolean);

  const temDistribuicao = canais.length > 0 || tipos.length > 0;
  const temReferencias = demanda.arquivo_url || meios.length > 0;
  const temArte = demanda.arte_local || demanda.arte_hora || demanda.arte_logos;

  return (
    <div className="card">
      <div className="card-body detalhe-secoes">
        {/* Objetivo — sempre visível, em destaque. Editável pela Comunicação. */}
        <Secao
          icon={Target}
          titulo="Objetivo"
          action={podeEditar && !editObj && (
            <button className="btn btn-ghost btn-sm" onClick={abrirEdicaoObjetivo} style={{ minHeight: 26, padding: '4px 10px' }}>
              <Pencil size={12} /> Editar
            </button>
          )}
        >
          {editObj ? (
            <div className="stack" style={{ gap: 8 }}>
              <textarea
                className="textarea"
                rows={4}
                value={objetivo}
                onChange={(e) => setObjetivo(e.target.value)}
                autoFocus
              />
              {erroObj && <div className="text-sm" style={{ color: 'var(--c-danger)' }}>{erroObj}</div>}
              <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditObj(false)} disabled={savingObj}>
                  <X size={12} /> Cancelar
                </button>
                <button className="btn btn-primary btn-sm" onClick={salvarObjetivo} disabled={savingObj}>
                  <Save size={12} /> {savingObj ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          ) : (
            <p className="detalhe-texto">{demanda.objetivo}</p>
          )}
        </Secao>

        {/* Briefing + Datas lado a lado quando ambos existem */}
        {(briefing.length > 0 || datas.length > 0) && (
          <div className="detalhe-grid">
            {briefing.length > 0 && (
              <Secao icon={FileText} titulo="Briefing">
                {briefing.map(({ label, value, icon: Icon }) => (
                  <CampoLinha key={label} label={label} icon={Icon}>{value}</CampoLinha>
                ))}
              </Secao>
            )}
            {datas.length > 0 && (
              <Secao icon={Calendar} titulo="Datas">
                {datas.map(({ label, value }) => (
                  <CampoLinha key={label} label={label}>{value}</CampoLinha>
                ))}
              </Secao>
            )}
          </div>
        )}

        {/* Distribuição */}
        {temDistribuicao && (
          <Secao icon={Radio} titulo="Distribuição">
            {canais.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                <span className="detalhe-rotulo">Canais</span>
                <div className="row-wrap" style={{ gap: 4 }}>
                  {canais.map((l) => <Badge key={l} color="info">{l}</Badge>)}
                </div>
              </div>
            )}
            {tipos.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span className="detalhe-rotulo">Formato</span>
                <div className="row-wrap" style={{ gap: 4 }}>
                  {tipos.map((l) => <Badge key={l} color="purple">{l}</Badge>)}
                </div>
              </div>
            )}
          </Secao>
        )}

        {/* Necessário para a arte — pedido da Comunicação: local/hora/logos
            sempre preenchidos (ou "Não informado"), pra quem for montar a
            peça nunca ficar na dúvida se o dado existe. */}
        {temArte && (
          <Secao icon={FileText} titulo="Necessário para a arte">
            {demanda.arte_local && <CampoLinha label="Local">{demanda.arte_local}</CampoLinha>}
            {demanda.arte_hora && <CampoLinha label="Hora">{demanda.arte_hora}</CampoLinha>}
            {demanda.arte_logos && <CampoLinha label="Logos">{demanda.arte_logos}</CampoLinha>}
          </Secao>
        )}

        {/* Detalhes adicionais — antes ficava atrás de "Ver mais"; agora sempre
            visível porque normalmente é onde estão horários, pessoas envolvidas,
            locais — info que a Comunicação precisa pra produzir */}
        {demanda.detalhes && (
          <Secao icon={FileText} titulo="Detalhes adicionais">
            <p className="detalhe-texto">{demanda.detalhes}</p>
          </Secao>
        )}

        {/* Materiais e referências */}
        {temReferencias && (
          <Secao icon={Link2} titulo="Materiais e referências">
            {demanda.arquivo_url && (
              <CampoLinha label="Link">
                {isUrl(demanda.arquivo_url) ? (
                  <a href={demanda.arquivo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', wordBreak: 'break-all' }}>
                    {demanda.arquivo_url}
                  </a>
                ) : demanda.arquivo_url}
              </CampoLinha>
            )}
            {meios.map(({ label, value }) => (
              <CampoLinha key={label} label={label}>
                {isUrl(value) ? (
                  <a href={value} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', wordBreak: 'break-all' }}>{value}</a>
                ) : value}
              </CampoLinha>
            ))}
          </Secao>
        )}

        {/* Observações ficam num expansível discreto — é metadado de
            importação (hash do script), não conteúdo da demanda */}
        {demanda.observacoes && <Observacoes valor={demanda.observacoes} />}
      </div>
    </div>
  );
}

function Secao({ icon: Icon, titulo, action, children }) {
  return (
    <section className="detalhe-secao">
      <header className="detalhe-secao-header">
        {Icon && <Icon size={13} />}
        <span>{titulo}</span>
        {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </header>
      <div className="detalhe-secao-body">{children}</div>
    </section>
  );
}

function CampoLinha({ label, icon: Icon, children }) {
  return (
    <div className="detalhe-campo">
      <div className="detalhe-rotulo">
        {Icon && <Icon size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
        {label}
      </div>
      <div className="detalhe-valor">{children}</div>
    </div>
  );
}

function Observacoes({ valor }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="detalhe-obs">
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAberto((v) => !v)} style={{ padding: '4px 8px', minHeight: 26, fontSize: 11 }}>
        {aberto ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {aberto ? 'Ocultar observações internas' : 'Ver observações internas'}
      </button>
      {aberto && <p className="detalhe-texto" style={{ marginTop: 6, fontSize: 12, color: 'var(--c-text-muted)' }}>{valor}</p>}
    </div>
  );
}
